import { Injectable } from '@angular/core';
import { ROUTES } from '@core/constants';
import { IResponseHttp } from '@core/models';
import { Global } from '../global/global.service';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private readonly api: ApiService,
    private readonly global: Global,
  ) {}

  setSession({
    accessToken,
    refreshToken,
    email,
    fullName,
    userId,
    scope,
    emailVerified,
    workshopId,
    roles,
    forcePasswordChange,
  }: {
    accessToken: string;
    refreshToken?: string;
    email: string;
    fullName: string;
    userId: string;
    scope?: 'full' | 'checkout';
    emailVerified?: boolean;
    workshopId?: string;
    roles?: string[];
    forcePasswordChange?: boolean;
  }): void {
    const resolvedScope = scope || this.extractScopeFromToken(accessToken) || 'full';
    const resolvedEmailVerified =
      typeof emailVerified === 'boolean'
        ? emailVerified
        : this.extractEmailVerifiedFromToken(accessToken);

    sessionStorage.setItem('access_token', accessToken);
    sessionStorage.setItem('refresh_token', refreshToken ?? '');
    sessionStorage.setItem('user_email', email);
    sessionStorage.setItem('user_fullname', fullName);
    sessionStorage.setItem('user_id', userId);
    sessionStorage.setItem('auth_scope', resolvedScope);
    sessionStorage.setItem('workshop_id', workshopId ?? '');
    sessionStorage.setItem(
      'force_password_change',
      forcePasswordChange ? 'true' : 'false',
    );
    sessionStorage.setItem(
      'email_verified',
      resolvedEmailVerified ? 'true' : 'false',
    );
    sessionStorage.setItem('user_roles', JSON.stringify(roles ?? []));

    this.global.setIsLogged(true);
    this.global.setEmail(email);
    this.global.setUser(fullName);
    this.global.setUserName(fullName);
    this.global.setIdUser(userId);
    this.global.setCompanyId(workshopId ?? '');
    this.global.setRol(roles ?? []);
  }

  restoreSession(): void {
    const accessToken = sessionStorage.getItem('access_token');
    const email = sessionStorage.getItem('user_email');
    const fullName = sessionStorage.getItem('user_fullname');
    const userId = sessionStorage.getItem('user_id');
    const workshopId = sessionStorage.getItem('workshop_id') ?? '';
    const roles = this.readStoredRoles();

    if (!accessToken || !email || !fullName || !userId) return;
    if (!this.hasValidToken()) {
      this.clearSession();
      return;
    }

    this.global.setIsLogged(true);
    this.global.setEmail(email);
    this.global.setUser(fullName);
    this.global.setUserName(fullName);
    this.global.setIdUser(userId);
    this.global.setCompanyId(workshopId);
    this.global.setRol(roles);
  }

  hasValidToken(): boolean {
    const token = sessionStorage.getItem('access_token');
    if (!token) return false;

    const payload = this.decodeTokenPayload(token);
    const exp = Number(payload?.['exp'] || 0);
    if (!exp) return true;
    return exp > Math.floor(Date.now() / 1000);
  }

  clearSession(): void {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('user_id');
    sessionStorage.removeItem('user_fullname');
    sessionStorage.removeItem('user_email');
    sessionStorage.removeItem('auth_scope');
    sessionStorage.removeItem('email_verified');
    sessionStorage.removeItem('workshop_id');
    sessionStorage.removeItem('user_roles');
    sessionStorage.removeItem('force_password_change');
    this.global.reset();
  }

  mustChangePassword(): boolean {
    return sessionStorage.getItem('force_password_change') === 'true';
  }

  setPasswordChangeRequired(required: boolean): void {
    sessionStorage.setItem('force_password_change', required ? 'true' : 'false');
  }

  getStoredRoles(): string[] {
    return this.readStoredRoles();
  }

  getAuthScope(): 'full' | 'checkout' {
    const scope = sessionStorage.getItem('auth_scope');
    return scope === 'checkout' ? 'checkout' : 'full';
  }

  hasProfileAccess(): boolean {
    return this.getAuthScope() === 'full';
  }

  isCheckoutScoped(): boolean {
    return this.getAuthScope() === 'checkout';
  }

  async loginWithDocument(
    document: string,
    password: string,
  ): Promise<IResponseHttp> {
    return this.api.post<IResponseHttp>(ROUTES.AUTH_LOGIN, {
      document,
      password,
    });
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<IResponseHttp> {
    return this.api.post<IResponseHttp>(ROUTES.AUTH_CHANGE_PASSWORD, {
      currentPassword,
      newPassword,
    });
  }

  getAccessToken(): string {
    return sessionStorage.getItem('access_token') ?? '';
  }

  getRefreshToken(): string {
    return sessionStorage.getItem('refresh_token') ?? '';
  }

  setAccessToken(token: string): void {
    sessionStorage.setItem('access_token', token);
  }

  setRefreshToken(token: string): void {
    sessionStorage.setItem('refresh_token', token);
  }

  async refreshAccessToken(refreshToken: string): Promise<{
    accessToken: string;
    refreshToken: string;
  }> {
    const response = await this.api.post<IResponseHttp>(ROUTES.AUTH_REFRESH, { refreshToken });
    const payload = (response?.payload as Record<string, unknown> | undefined) ?? {};
    const accessToken =
      (typeof response?.accessToken === 'string' ? response.accessToken : '') ||
      (typeof payload['accessToken'] === 'string' ? payload['accessToken'] : '');
    const nextRefreshToken =
      (typeof response?.refreshToken === 'string' ? response.refreshToken : '') ||
      (typeof payload['refreshToken'] === 'string' ? payload['refreshToken'] : '');

    if (!accessToken || !nextRefreshToken) {
      throw new Error('Respuesta inválida al refrescar token');
    }
    this.setAccessToken(accessToken);
    this.setRefreshToken(nextRefreshToken);
    return { accessToken, refreshToken: nextRefreshToken };
  }

  async logout(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return;
    await this.api.post<IResponseHttp>(ROUTES.AUTH_LOGOUT, { refreshToken });
  }

  private decodeTokenPayload(token: string): Record<string, unknown> | null {
    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) return null;
      const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
      const payload = JSON.parse(atob(padded));
      return payload as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private extractScopeFromToken(token: string): 'full' | 'checkout' | null {
    const payload = this.decodeTokenPayload(token);
    const scope = payload?.['scope'];
    if (scope === 'checkout') return 'checkout';
    if (scope === 'full') return 'full';
    return null;
  }

  private extractEmailVerifiedFromToken(token: string): boolean {
    const payload = this.decodeTokenPayload(token);
    return payload?.['emailVerified'] !== false;
  }

  private readStoredRoles(): string[] {
    try {
      const raw = sessionStorage.getItem('user_roles');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((role): role is string => typeof role === 'string')
        : [];
    } catch {
      return [];
    }
  }
}
